import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Users, Target, Wallet, CalendarClock, AlertCircle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";
import PeriodFilter, { computeRange } from "@/components/PeriodFilter";
import InstallPrompt from "@/components/InstallPrompt";
import { STATUS_HEX, ACCENT_PALETTE } from "@/lib/constants";
import UpcomingMeetings from "@/components/UpcomingMeetings";

const COLORS = ACCENT_PALETTE;

function KpiCard({ label, value, sub, icon: Icon, tone = "default", accent = "blue", testid }) {
  const toneClasses = {
    default: "text-slate-900",
    up: "text-emerald-600",
    down: "text-rose-600",
    accent: "text-[#1D63ED]",
  };
  const accentClasses = {
    blue:    "bg-blue-100 border-blue-200 text-blue-600",
    emerald: "bg-emerald-100 border-emerald-200 text-emerald-600",
    amber:   "bg-amber-100 border-amber-200 text-amber-600",
    rose:    "bg-rose-100 border-rose-200 text-rose-600",
    violet:  "bg-violet-100 border-violet-200 text-violet-600",
    indigo:  "bg-indigo-100 border-indigo-200 text-indigo-600",
  };
  return (
    <div className="solid-panel p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
          <p className={`text-2xl md:text-3xl font-semibold mt-2 ${toneClasses[tone]}`}>{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-2">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${accentClasses[accent] || accentClasses.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState({ key: "month", ...computeRange("month") });
  const isAdmin = user?.role === "superadmin";

  useEffect(() => {
    const params = {};
    if (period.start) params.start = period.start;
    if (period.end) params.end = period.end;
    api.get("/analytics/overview", { params }).then((r) => setData(r.data));
  }, [period.start, period.end]);

  const statusData = data ? Object.entries(data.status_counts).map(([k, v]) => ({ name: k, value: v })) : [];
  const sourceData = data ? Object.entries(data.source_counts).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="space-y-6" data-testid="overview-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Dashboard</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">
            {isAdmin ? "Command Centre" : `Hey, ${user?.name?.split(" ")[0]}`}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isAdmin
              ? "A live pulse across your entire sales floor — leads, members and revenue."
              : "Your leads, your pipeline. Move fast and close smart."}
            <span className="ml-2 text-slate-400">· {period.label}</span>
          </p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} testid="dashboard-period" />
      </header>

      <InstallPrompt />

      {!data ? (
        <div className="text-slate-500">Loading analytics…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testid="kpi-total" label={period.key === "all" ? "Total Leads" : "New Leads"} value={data.total} sub={period.label} icon={Users} tone="accent" accent="blue" />
            <KpiCard testid="kpi-won" label="Won" value={data.won} sub={`Conversion ${data.conversion_rate}%`} icon={TrendingUp} tone="up" accent="emerald" />
            <KpiCard testid="kpi-pipeline" label="Pipeline Value" value={`₹${(data.pipeline_value || 0).toLocaleString()}`} sub={`Won ${period.label}: ₹${(data.won_value || 0).toLocaleString()}`} icon={Wallet} accent="violet" />
            <KpiCard testid="kpi-followups" label="Follow-ups Due" value={data.overdue_followups + data.upcoming_followups} sub={`${data.overdue_followups} overdue • ${data.upcoming_followups} upcoming`} icon={CalendarClock} tone={data.overdue_followups > 0 ? "down" : "default"} accent={data.overdue_followups > 0 ? "rose" : "amber"} />
          </div>

          <UpcomingMeetings />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="solid-panel p-6 lg:col-span-2" data-testid="status-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold font-semibold">Pipeline by stage</h2>
                  <p className="text-xs text-slate-500">Distribution across current stages</p>
                </div>
                <Link to="/dashboard/kanban" className="text-xs text-[#1D63ED] hover:underline">Open Kanban →</Link>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #ecebe4", borderRadius: 12, color: "#1e1e22" }} />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {statusData.map((s) => (
                        <Cell key={s.name} fill={STATUS_HEX[s.name] || "#1D63ED"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="solid-panel p-6" data-testid="source-chart">
              <h2 className="text-sm font-semibold font-semibold mb-1">Lead sources</h2>
              <p className="text-xs text-slate-500 mb-4">Where leads originate from</p>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                      {sourceData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #ecebe4", borderRadius: 12, color: "#1e1e22" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {sourceData.map((s, i) => (
                  <div key={s.name} className="flex items-center gap-1.5 text-xs text-slate-700">
                    <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {s.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {isAdmin && data.member_performance?.length > 0 && (
            <div className="solid-panel p-6" data-testid="member-performance">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-[#1D63ED]" />
                <h2 className="text-sm font-semibold font-semibold">Team performance</h2>
              </div>
              <div className="space-y-3">
                {data.member_performance.map((m, idx) => {
                  const max = data.member_performance[0]?.count || 1;
                  const pct = Math.round((m.count / max) * 100);
                  const color = ACCENT_PALETTE[idx % ACCENT_PALETTE.length];
                  return (
                    <div key={m.user_id || "unassigned"}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="font-medium">{m.name}</span>
                        <span className="text-slate-600">{m.count} leads</span>
                      </div>
                      <div className="h-2 bg-slate-100/70 rounded-full overflow-hidden">
                        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.overdue_followups > 0 && (
            <div className="glass rounded-2xl p-4 md:p-5 flex items-start gap-3 md:gap-4 border-l-4 border-red-500/60 flex-wrap sm:flex-nowrap">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{data.overdue_followups} follow-ups are overdue</p>
                <p className="text-sm text-slate-600">Don't let hot leads cool down.</p>
              </div>
              <Link to="/dashboard/followups" className="text-sm text-[#1D63ED] hover:underline whitespace-nowrap">Review now →</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
